/**
 * Disambiguates role labels that collide within a single dropdown's own option list.
 *
 * Two roles can share a label (e.g. GROUP-level VIEW_ONLY and ORG-level ORG_VIEW_ONLY
 * both read "View Only"). Picking the wrong one is a real over-grant (org-wide read vs
 * single-group read), so any dropdown that lists roles without qualifying duplicates is
 * a latent authorization bug, not just a cosmetic one.
 *
 * Ambiguity is computed against the list a caller passes in, not the global role
 * catalogue — a dropdown that has already been filtered down to one role per label has
 * nothing to disambiguate and must keep rendering bare labels.
 */

/** The minimal shape this helper needs from a role to render its dropdown label. */
export interface RoleLabelInput {
  key: string;
  label: string;
  level?: string;
}

/** Human-readable qualifier for a role's level, used to disambiguate same-label roles. */
const LEVEL_WORDS: Record<string, string> = {
  org: "organization",
  group: "group",
  all: "platform",
};

/**
 * Builds a role-key -> display-label resolver scoped to `roles`. Labels are qualified
 * with their level ONLY when they collide within that list.
 *
 * The qualifier fallback never drops back to the bare label: an unrecognized level
 * string is shown as-is, and a role with no level at all is qualified with its own key.
 * Either way, a duplicate label always renders distinguishable from its sibling.
 */
export function resolveRoleLabels(roles: readonly RoleLabelInput[]): (key: string) => string {
  const labelCounts = new Map<string, number>();
  for (const r of roles) {
    labelCounts.set(r.label, (labelCounts.get(r.label) ?? 0) + 1);
  }

  const displayLabels = new Map<string, string>();
  for (const r of roles) {
    const isAmbiguous = (labelCounts.get(r.label) ?? 0) > 1;
    displayLabels.set(r.key, isAmbiguous ? qualifyLabel(r) : r.label);
  }

  return (key: string): string => displayLabels.get(key) ?? key;
}

function qualifyLabel({ label, level, key }: RoleLabelInput): string {
  const qualifier = level ? (LEVEL_WORDS[level] ?? level) : key;
  return `${label} (${qualifier})`;
}
